import { IWebContent, IWebInteractionOutcome, TWebPageInteraction } from '../../generated/gql/graphql';
import { AppState, FlowGraph } from './AppState';
import { AppRunCommand, CanvasDisplaySelection, ChildAppClientState, ClientState } from './ClientState';

export type ExtensionAppStartupConfig = {
  tabId: number;
  sessionState: ExtClientState;
  appStateWithGraph?: AppState & { graph: FlowGraph };
};

export type GetStartupConfig = {
  action: "getStartupConfig";
  tabId?: number;
};

export type Authenticate = {
  action: 'authenticate';
  localHost?: boolean;
};

export type NewSession = {
  action: 'newSession';
  flowId: string;
  appStateWithGraph?: AppState & { graph: FlowGraph };
  localHost?: boolean;
  runInBackground?: boolean;
};

export type ResumeSession = {
  action: 'resumeSession';
  flowId?: string;
  clientId: string;
  appStateWithGraph?: AppState & { graph: FlowGraph };
  localHost?: boolean;
  runInBackground?: boolean;
};

export type UpdateChildClientState = {
  action: 'childAppClientStateUpdateRequest';
  // tabId should be added to the state in serviceWorker => contentScript (for parent app)
  // then also present in the state from contentScript => window listener (for parent app)
  // tabId would not be present in the state from sidePanel (child app) => serviceWorker
  // tabId would not be present for window (child app) => window listener (parent app)
  clientState: Partial<ChildAppClientState>;
  parentTabId: number;
};

export type UpdateClientState = {
  action: 'clientStateUpdateRequest';
  clientState: Partial<ClientState>;
  appState?: AppState & { graph: FlowGraph };
  tabId: number;
  localhost?: boolean;
};

export type CommandToSidePanel = {
  action: 'commandToSidePanel';
  command: {
    type: 'appRunCommand',
    command: AppRunCommand,
  } | {
    type: 'changeCanvasDisplay',
    display: CanvasDisplaySelection | null,
  }
}

export function isCommandToSidePanel(request: ChromeExtensionRequest): request is CommandToSidePanel {
  return request.action === 'commandToSidePanel';
}

export function isUpdateChildClientState(request: ChromeExtensionRequest): request is UpdateChildClientState {
  return request.action === 'childAppClientStateUpdateRequest';
}

export function isUpdateClientState(request: ChromeExtensionRequest): request is UpdateClientState {
  return request.action === 'clientStateUpdateRequest';
}

export type ServiceWorkerRequest =
  | GetStartupConfig
  | Authenticate
  | NewSession
  | ResumeSession
  | UpdateChildClientState
  | UpdateClientState

// request to service worker, side panel and/or popup
export type ChromeExtensionRequest =
  | ServiceWorkerRequest
  | CommandToSidePanel;

export type ErrorResponse = {
  status: 'error';
  message: string;
}

export type GetStartupConfigServiceWorkerResponse = {
  status: 'success';
  action: 'getStartupConfig';
  config: ExtensionAppStartupConfig;
} | {
  status: 'warning';
  action: 'getStartupConfig';
  tabId: number;
  useLocalhost?: boolean;
} | ErrorResponse;

export type AuthenticateServiceWorkerResponse = {
  status: 'success';
  action: 'authenticate';
  authToken: string;
} | ErrorResponse;

export type NewSessionServiceWorkerResponse = {
  status: 'success';
  action: 'newSession';
} | ErrorResponse;

export type ResumeSessionServiceWorkerResponse = {
  status: 'success';
  action: 'resumeSession';
} | ErrorResponse;

export type ChildAppClientStateUpdateResponse = {
  status: 'success';
  action: 'childAppClientStateUpdateRequest';
} | ErrorResponse;

export type ClientStateUpdateResponse = {
  status: 'success';
  action: 'clientStateUpdateRequest';
} | ErrorResponse;

export type SuccessResponse = {
  status: 'success';
}

export type ServiceWorkerResponse =
  | GetStartupConfigServiceWorkerResponse
  | AuthenticateServiceWorkerResponse
  | NewSessionServiceWorkerResponse
  | ResumeSessionServiceWorkerResponse
  | ChildAppClientStateUpdateResponse
  | ClientStateUpdateResponse

export type ChromeExtensionResponse =
  | ServiceWorkerResponse
  | SuccessResponse
  | ErrorResponse;

export type ChromeExtensionResponseSender = (response: ChromeExtensionResponse) => void;
export type ChromeExtensionRequestHandler = (message: ChromeExtensionRequest, sender: chrome.runtime.MessageSender, sendResponse: ChromeExtensionResponseSender) => boolean;

export type ExtClientState = ClientState & { localhost?: boolean; parentTabId?: number; replayEffectsOnConnect?: boolean; };
// IMPORTANT: never store sensitive data in chrome storage
// e.g. authToken should always be produced security in-time during runtime
export type NavigationContentScriptRequest = {
  __typename: 'NavigateToUrl',
  url: string;
}
export type CaptureWebContentScriptRequest = {
  __typename: 'CaptureWebContent',
}
export type HandshakeContentScriptRequest = {
  __typename: 'Handshake',
}
export type ContentScriptRequest = NavigationContentScriptRequest | TWebPageInteraction | CaptureWebContentScriptRequest | HandshakeContentScriptRequest;

export type NavigationContentScriptResponse = {
  __typename: 'NavigateToUrl',
  status: 'success';
} | ErrorResponse;
export type CaptureWebContentScriptResponse = (IWebContent & { __typename: 'WebContent' }) | ErrorResponse;
export type WebInteractionContentScriptResponse = (IWebInteractionOutcome & { __typename: 'WebInteractionOutcome' }) | ErrorResponse;
export type HandshakeContentScriptResponse = { __typename: 'Handshake', status: 'success' };
export type ContentScriptResponse = NavigationContentScriptResponse | WebInteractionContentScriptResponse | CaptureWebContentScriptResponse | HandshakeContentScriptResponse;

export type ChromeExtensionState = {
  // popup: {
  //   // TODO store selected flow Id by workspace
  //   selectedFlowId?: string,
  //   dev?: boolean,
  // }
  sessions: {
    [tabId: number]: ExtClientState;
  };
  appStateWithGraphs?: {
    [tabId: number]: AppState & { graph: FlowGraph; };
  };
};
